import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import {
  loadChildStats,
  findNewlyEarnedBadges,
  type Badge,
} from '@/services/badgeService'
import type {
  Mission,
  Skill,
  CurriculumTrack,
  AgeRange,
} from '@/types/database'

// =============================================================================
// TYPES
// =============================================================================

export interface MissionWithSkills extends Mission {
  mission_skill_map: Array<{
    weight: number
    skills: Skill
  }>
}

export interface MissionStatus {
  mission: MissionWithSkills
  isCompleted: boolean
  isStarted: boolean
  wordCount: number
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Returns the curriculum track for today based on day of week.
 * Monday=structure, Tuesday=craft, Wednesday=revision, Thursday=reading
 * Friday=null (choice day), Saturday/Sunday=null (weekend)
 */
function getTodayTrack(): CurriculumTrack | null {
  const day = new Date().getDay()
  const tracks: (CurriculumTrack | null)[] = [
    null,        // 0 Sunday - no mission
    'structure', // 1 Monday
    'craft',     // 2 Tuesday
    'revision',  // 3 Wednesday
    'reading',   // 4 Thursday
    null,        // 5 Friday - choice day (handled separately)
    null,        // 6 Saturday - no mission
  ]
  return tracks[day]
}

/**
 * Returns true if today is Friday (choice day)
 */
function isFriday(): boolean {
  return new Date().getDay() === 5
}

/**
 * Returns true if today is a weekend (Saturday or Sunday)
 */
function isWeekend(): boolean {
  const day = new Date().getDay()
  return day === 0 || day === 6
}

/**
 * Returns the current curriculum week number (1-6, cycling).
 * Based on weeks since a fixed start date.
 */
function getCurrentWeekNumber(): number {
  const startDate = new Date('2024-01-01')
  const now = new Date()
  const msPerWeek = 7 * 24 * 60 * 60 * 1000
  const weeksSinceStart = Math.floor((now.getTime() - startDate.getTime()) / msPerWeek)
  return (weeksSinceStart % 6) + 1 // weeks 1-6
}

/**
 * Converts age_range to grade for word goal selection
 */
function ageRangeToGrade(ageRange: AgeRange | null): 5 | 7 {
  return ageRange === '12-14' ? 7 : 5
}

/**
 * Returns ISO date string for start of current week (Monday)
 */
function getWeekStartISO(): string {
  const now = new Date()
  const day = now.getDay()
  const diff = now.getDate() - day + (day === 0 ? -6 : 1) // Monday
  const monday = new Date(now)
  monday.setDate(diff)
  monday.setHours(0, 0, 0, 0)
  return monday.toISOString()
}

/**
 * Returns ISO date string for end of current week (Sunday)
 */
function getWeekEndISO(): string {
  const now = new Date()
  const day = now.getDay()
  const diff = now.getDate() - day + (day === 0 ? 0 : 7) // Sunday
  const sunday = new Date(now)
  sunday.setDate(diff)
  sunday.setHours(23, 59, 59, 999)
  return sunday.toISOString()
}

/**
 * Returns ISO date strings for today's boundaries
 */
function getTodayBoundaries(): { start: string; end: string } {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const start = today.toISOString()
  today.setHours(23, 59, 59, 999)
  const end = today.toISOString()
  return { start, end }
}

// =============================================================================
// READ FUNCTIONS
// =============================================================================

/**
 * Gets today's mission for a child.
 * - Weekdays (Mon-Thu): Returns a mission for today's track, picking the next
 *   uncompleted one based on progression (week_number, order_in_week)
 * - Friday: Returns the first incomplete mission from any track (choice day)
 * - Weekend: Returns null
 */
export async function getTodaysMission(
  childId: string,
  ageRange: AgeRange | null
): Promise<MissionStatus | null> {
  if (!isSupabaseConfigured()) return null

  // Weekend - no mission
  if (isWeekend()) return null

  const { start: todayStart, end: todayEnd } = getTodayBoundaries()

  // Friday choice day - find first incomplete mission from any track
  if (isFriday()) {
    return getFridayChoiceMission(childId, ageRange)
  }

  // Regular weekday - get mission for today's track
  const track = getTodayTrack()
  if (!track) return null

  // Get all missions for this track, ordered by progression
  // Also get child's completions to find the next uncompleted mission
  const [missionsResult, allCompletionsResult, todayCompletionsResult, progressResult] =
    await Promise.all([
      supabase
        .from('missions')
        .select(
          `
          *,
          mission_skill_map (
            weight,
            skills (*)
          )
        `
        )
        .eq('track', track)
        .order('week_number')
        .order('order_in_week'),
      // All completions for this child (to find next mission in progression)
      supabase.from('mission_completions').select('mission_id').eq('child_id', childId),
      // Today's completions (to check if today's mission is done)
      supabase
        .from('mission_completions')
        .select('mission_id')
        .eq('child_id', childId)
        .gte('completed_at', todayStart)
        .lte('completed_at', todayEnd),
      // Get active (uncompleted) mission progress
      // Invariant: one active mission at a time per child
      supabase
        .from('mission_progress')
        .select('mission_id, words_written')
        .eq('child_id', childId)
        .is('completed_at', null)
        .order('started_at', { ascending: false })
        .limit(1),
    ])

  if (missionsResult.error || !missionsResult.data?.length) {
    console.error('No missions found for track:', track, missionsResult.error)
    return null
  }

  const missions = missionsResult.data as MissionWithSkills[]
  const completedIds = new Set((allCompletionsResult.data || []).map((c) => c.mission_id))
  const todayCompletedIds = new Set((todayCompletionsResult.data || []).map((c) => c.mission_id))
  const activeProgress = progressResult.data?.[0]

  // Find the first uncompleted mission in this track's progression
  // If all are completed, cycle back to the first one
  let mission = missions.find((m) => !completedIds.has(m.id))
  if (!mission) {
    // All missions completed - offer the first one again
    mission = missions[0]
  }

  const isCompleted = todayCompletedIds.has(mission.id)
  const isStarted = activeProgress?.mission_id === mission.id

  return {
    mission,
    isCompleted,
    isStarted,
    wordCount: isStarted ? activeProgress?.words_written || 0 : 0,
  }
}

/**
 * Friday choice day: returns the first incomplete mission from any track
 */
async function getFridayChoiceMission(
  childId: string,
  _ageRange: AgeRange | null
): Promise<MissionStatus | null> {
  const { start: todayStart, end: todayEnd } = getTodayBoundaries()

  // Get all missions and completions to find the next uncompleted one
  const [missionsResult, allCompletionsResult, todayCompletionsResult, progressResult] =
    await Promise.all([
      supabase
        .from('missions')
        .select(
          `
          *,
          mission_skill_map (
            weight,
            skills (*)
          )
        `
        )
        .order('track')
        .order('week_number')
        .order('order_in_week'),
      supabase.from('mission_completions').select('mission_id').eq('child_id', childId),
      supabase
        .from('mission_completions')
        .select('mission_id')
        .eq('child_id', childId)
        .gte('completed_at', todayStart)
        .lte('completed_at', todayEnd),
      supabase
        .from('mission_progress')
        .select('mission_id, words_written')
        .eq('child_id', childId)
        .is('completed_at', null)
        .order('started_at', { ascending: false })
        .limit(1),
    ])

  if (missionsResult.error || !missionsResult.data?.length) {
    return null
  }

  const missions = missionsResult.data as MissionWithSkills[]
  const completedIds = new Set((allCompletionsResult.data || []).map((c) => c.mission_id))
  const todayCompletedIds = new Set((todayCompletionsResult.data || []).map((c) => c.mission_id))
  const activeProgress = progressResult.data?.[0]

  // Find first incomplete mission across all tracks
  let mission = missions.find((m) => !completedIds.has(m.id))
  if (!mission) {
    // All missions completed - offer the first one again
    mission = missions[0]
  }

  const isCompleted = todayCompletedIds.has(mission.id)
  const isStarted = activeProgress?.mission_id === mission.id

  return {
    mission,
    isCompleted,
    isStarted,
    wordCount: isStarted ? activeProgress?.words_written || 0 : 0,
  }
}

/**
 * Gets all missions for the current week with completion status
 */
export async function getThisWeeksMissions(
  childId: string,
  _ageRange: AgeRange | null
): Promise<MissionStatus[]> {
  if (!isSupabaseConfigured()) return []

  const weekNumber = getCurrentWeekNumber()
  const weekStart = getWeekStartISO()
  const weekEnd = getWeekEndISO()

  const [missionsResult, completionsResult] = await Promise.all([
    supabase
      .from('missions')
      .select(`
        *,
        mission_skill_map (
          weight,
          skills (*)
        )
      `)
      .eq('week_number', weekNumber)
      .order('track')
      .order('order_in_week'),
    supabase
      .from('mission_completions')
      .select('mission_id')
      .eq('child_id', childId)
      .gte('completed_at', weekStart)
      .lte('completed_at', weekEnd),
  ])

  if (missionsResult.error) {
    console.error('Error fetching week missions:', missionsResult.error)
    return []
  }

  const missions = (missionsResult.data || []) as MissionWithSkills[]
  const completedIds = new Set((completionsResult.data || []).map((c) => c.mission_id))

  return missions.map((mission) => ({
    mission,
    isCompleted: completedIds.has(mission.id),
    isStarted: false, // We don't track this at the week level
    wordCount: 0,
  }))
}

/**
 * Gets the appropriate word goal for a mission based on child's age/grade
 */
export function getWordGoalForGrade(mission: Mission, ageRange: AgeRange | null): number {
  const grade = ageRangeToGrade(ageRange)
  if (grade === 7 && mission.word_goal_7th) return mission.word_goal_7th
  if (grade === 5 && mission.word_goal_5th) return mission.word_goal_5th
  return mission.word_goal
}

/**
 * Returns display info for today's mission day type
 */
export function getTodayMissionType(): {
  type: 'weekday' | 'friday' | 'weekend'
  track: CurriculumTrack | null
  label: string
} {
  if (isWeekend()) {
    return { type: 'weekend', track: null, label: 'Free Day' }
  }
  if (isFriday()) {
    return { type: 'friday', track: null, label: 'Choice Day' }
  }
  const track = getTodayTrack()
  const trackLabels: Record<CurriculumTrack, string> = {
    structure: 'Story Structure',
    craft: 'Style & Craft',
    revision: 'Revision',
    reading: 'Reading & Response',
  }
  return {
    type: 'weekday',
    track,
    label: track ? trackLabels[track] : 'Writing',
  }
}

// =============================================================================
// WRITE FUNCTIONS
// =============================================================================

/**
 * Starts a mission for a child.
 * Creates a mission_progress entry and sets linked skills to 'in_progress'.
 *
 * Invariant: one active mission at a time per child.
 * Starting a new mission does not automatically complete the previous one.
 */
export async function startMission(
  childId: string,
  missionId: string
): Promise<{ success: boolean; error?: string }> {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Database not configured' }
  }

  try {
    // Create mission_progress entry
    const { error: progressError } = await supabase.from('mission_progress').insert({
      child_id: childId,
      mission_id: missionId,
    } as Record<string, unknown>)

    if (progressError) {
      console.error('Error starting mission:', progressError)
      return { success: false, error: progressError.message }
    }

    // Get linked skills and update their status to 'in_progress'
    const { data: skillMap } = await supabase
      .from('mission_skill_map')
      .select('skill_id')
      .eq('mission_id', missionId)

    if (skillMap && skillMap.length > 0) {
      // Upsert skill progress for each linked skill
      const upserts = skillMap.map((s) => ({
        child_id: childId,
        skill_id: s.skill_id,
        status: 'in_progress' as const,
        last_practiced_at: new Date().toISOString(),
      }))

      const { error: skillError } = await supabase
        .from('child_skill_progress')
        .upsert(upserts, { onConflict: 'child_id,skill_id' })

      if (skillError) {
        console.error('Error updating skill progress:', skillError)
        // Non-fatal: mission started but skills not updated
      }
    }

    return { success: true }
  } catch (err) {
    console.error('Error in startMission:', err)
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' }
  }
}

/**
 * Completes a mission for a child.
 * - Records completion in mission_completions (audit trail)
 * - Updates mission_progress with completed_at
 * - Updates linked skills: increments practice_count, may set to 'mastered'
 *
 * Mastery rule: skill is mastered when practice_count >= 4
 * (weights from mission_skill_map contribute to practice_count)
 */
export async function completeMission(
  childId: string,
  missionId: string,
  storyId: string | null,
  wordCount: number,
  checklistState: Record<string, boolean>
): Promise<{ success: boolean; error?: string }> {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Database not configured' }
  }

  try {
    // 1. Record completion in mission_completions (source of truth)
    const { error: completionError } = await supabase.from('mission_completions').insert({
      child_id: childId,
      mission_id: missionId,
      story_id: storyId,
      word_count: wordCount,
      checklist_state: checklistState,
    } as Record<string, unknown>)

    if (completionError) {
      // Could be duplicate (same mission same day) - that's OK
      if (!completionError.message.includes('unique')) {
        console.error('Error recording completion:', completionError)
        return { success: false, error: completionError.message }
      }
    }

    // 2. Update mission_progress to mark completed
    const { error: progressError } = await supabase
      .from('mission_progress')
      .update({
        completed_at: new Date().toISOString(),
        words_written: wordCount,
      } as Record<string, unknown>)
      .eq('child_id', childId)
      .eq('mission_id', missionId)
      .is('completed_at', null)

    if (progressError) {
      console.error('Error updating mission progress:', progressError)
      // Non-fatal: completion recorded
    }

    // 3. Update linked skills - increment practice_count, potentially master
    const { data: skillMap } = await supabase
      .from('mission_skill_map')
      .select('skill_id, weight')
      .eq('mission_id', missionId)

    if (skillMap && skillMap.length > 0) {
      await Promise.all(
        skillMap.map(async ({ skill_id, weight }) => {
          // Get current progress
          const { data: progress } = await supabase
            .from('child_skill_progress')
            .select('practice_count, status')
            .eq('child_id', childId)
            .eq('skill_id', skill_id)
            .single()

          const currentCount = progress?.practice_count || 0
          const newCount = currentCount + weight
          // Mastery at 4 points (e.g., one weight-3 mission + one weight-1, or two weight-2)
          const newStatus = newCount >= 4 ? 'mastered' : 'in_progress'

          await supabase.from('child_skill_progress').upsert(
            {
              child_id: childId,
              skill_id: skill_id,
              status: newStatus,
              practice_count: newCount,
              last_practiced_at: new Date().toISOString(),
              evidence_story_id: storyId,
            } as Record<string, unknown>,
            { onConflict: 'child_id,skill_id' }
          )
        })
      )
    }

    return { success: true }
  } catch (err) {
    console.error('Error in completeMission:', err)
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' }
  }
}

/**
 * Updates word count for an active mission (called during writing)
 */
export async function updateMissionWordCount(
  childId: string,
  missionId: string,
  wordCount: number
): Promise<void> {
  if (!isSupabaseConfigured()) return

  await supabase
    .from('mission_progress')
    .update({ words_written: wordCount } as Record<string, unknown>)
    .eq('child_id', childId)
    .eq('mission_id', missionId)
    .is('completed_at', null)
}

// =============================================================================
// CELEBRATION DATA
// =============================================================================

export interface MissionCompletionResult {
  success: boolean
  error?: string
  celebrationData?: {
    mission: Mission
    wordCount: number
    newBadges: Badge[]
    totalMissionsCompleted: number
  }
}

/**
 * Completes a mission and returns celebration data including newly earned badges.
 * This wraps the standard completeMission with additional stats gathering.
 */
export async function completeMissionWithCelebration(
  childId: string,
  mission: Mission,
  storyId: string | null,
  wordCount: number,
  checklistState: Record<string, boolean>
): Promise<MissionCompletionResult> {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Database not configured' }
  }

  try {
    // 1. Get stats BEFORE completion to compare for new badges
    const statsBefore = await loadChildStats(childId)

    // 2. Complete the mission
    const result = await completeMission(childId, mission.id, storyId, wordCount, checklistState)

    if (!result.success) {
      return result
    }

    // 3. Get stats AFTER completion
    const statsAfter = await loadChildStats(childId)

    // 4. Find newly earned badges
    const newBadges = statsBefore && statsAfter
      ? findNewlyEarnedBadges(statsBefore, statsAfter)
      : []

    // 5. Get total missions completed
    const totalMissionsCompleted = statsAfter?.missionsCompleted || 1

    return {
      success: true,
      celebrationData: {
        mission,
        wordCount,
        newBadges,
        totalMissionsCompleted,
      },
    }
  } catch (err) {
    console.error('Error in completeMissionWithCelebration:', err)
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' }
  }
}
